import { ReactNode } from 'react'
import MuiModal from '@mui/material/Modal'
import { Casing, TEXT_ROLE_CASING } from '@common/helpers/casing'
import cn from 'classnames'
import { DEFAULT_EMPTY_OBJECT_PROP } from '../../../constants/common'
import { useIsBoxViewerModalOpen } from '../../BoxViewer/boxViewerModalStore'
import { useIsFeedbackWidgetOpen } from '../../FeedbackWidget/feedbackWidgetOpenStore'
import Icon from '../Icon'
import { formatChildren } from '../Text'
import styles from './Modal.module.scss'

interface IProps {
  title?: ReactNode
  /** Overrides the title's case: `sentence` for a confirmation that asks a question. */
  titleCasing?: Casing
  open?: boolean
  children?: ReactNode
  onCancel?: () => void
  footer?: ReactNode
  classes?: Partial<Record<'root' | 'header' | 'close' | 'title' | 'body' | 'footer', string>>
  size?: string
  disableEnforceFocus?: boolean
  hideCloseIcon?: boolean
}

const Modal = ({
  classes = DEFAULT_EMPTY_OBJECT_PROP,
  title = '',
  titleCasing = TEXT_ROLE_CASING.modalTitle,
  open,
  onCancel,
  children,
  footer,
  size,
  disableEnforceFocus,
  hideCloseIcon = false,
}: IProps) => {
  // Yield the focus trap while a floating surface outside the dialog is up, the
  // FeedbackWidget panel or a Box preview opened over the dialog, since its inputs
  // would otherwise lose focus to the trap. An explicit prop wins.
  const isFeedbackWidgetOpen = useIsFeedbackWidgetOpen()
  const isBoxViewerModalOpen = useIsBoxViewerModalOpen()

  return (
    <MuiModal
      open={open}
      onClose={onCancel}
      disableEnforceFocus={disableEnforceFocus ?? (isFeedbackWidgetOpen || isBoxViewerModalOpen)}
    >
      <div
        className={cn(styles.modal, classes.root, {
          [styles.fullscreenModal]: size === 'fullscreen',
          [styles.smallModal]: size === 'small',
        })}
        data-cy="files-modal"
      >
        <div
          className={cn(
            {
              [styles.modalHeaderTitle]: title,
            },
            classes.header,
          )}
        >
          {!hideCloseIcon && (
            <Icon
              name="x"
              size="lg"
              aria-hidden={false}
              aria-label="Close"
              data-cy="modal-close-button"
              className={cn(styles.modalClose, classes.close)}
              onClick={onCancel}
            />
          )}
          {title && (
            <div className={cn(styles.modalTitle, classes.title)} data-cy="modal-title">
              {formatChildren(titleCasing, title)}
            </div>
          )}
        </div>

        <div className={cn(styles.modalBody, classes.body)}>{children}</div>
        {footer && <div className={cn(styles.modalFooter, classes.footer)}>{footer}</div>}
      </div>
    </MuiModal>
  )
}

export default Modal
